import { type RefinementCtx, z } from 'zod';
import { genderSchema } from '../const/genderSchema';
import { moneyPositiveIntSchema } from '../const/moneyPositiveIntSchema';
import { productArticleSchema } from '../const/producArticleSchema';
import { productDiscountTypeSchema } from '../const/productDiscountTypeSchema';
import { sellerPlatformSchema } from '../const/sellerPlatformSchema';
import { uuidSchema } from '../const/uuidSchema';

export const imageItemSchema = z.object({
    id: uuidSchema,
    position: z
        .number({
            required_error: 'нужно указать позицию картинки',
            invalid_type_error: 'позиция картинки должна быть числом',
        })
        .int('позиция картинки должна быть целым числом')
        .min(0, 'позиция картинки не может быть отрицательной'),
});

export const categoryLinkSchema = z.object({
    id: uuidSchema,
    order: z
        .number({ invalid_type_error: 'порядок должен быть числом' })
        .int('порядок должен быть целым числом')
        .nullable()
        .optional(),
});

export const selectionListLinkSchema = z.object({
    id: uuidSchema,
    order: z
        .number({ invalid_type_error: 'порядок должен быть числом' })
        .int('порядок должен быть целым числом')
        .nullable()
        .optional(),
});

export const priceItemSchema = z
    .object({
        rowId: uuidSchema,
        price: moneyPositiveIntSchema,
        discountType: productDiscountTypeSchema,
        discountPercent: z
            .number({ invalid_type_error: 'нужен процент скидки (1..99)' })
            .int('нужен процент скидки (1..99)')
            .min(1, 'нужен процент скидки (1..99)')
            .max(99, 'нужен процент скидки (1..99)')
            .nullable()
            .optional(),
    })
    .superRefine((p, ctx) => {
        if (p.discountType === 'None' && typeof p.discountPercent === 'number') {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['discountPercent'],
                message: 'процент не нужен при отсутствии типа скидки',
            });
        }

        if (p.discountType !== 'None' && p.discountPercent == null) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['discountPercent'],
                message: 'нужен процент скидки (1..99)',
            });
        }
    });

export const productObject = z.object({
    id: uuidSchema.optional(),

    name: z
        .string({
            required_error: 'укажите название товара',
            invalid_type_error: 'название должно быть строкой',
        })
        .trim()
        .min(1, 'введите название товара')
        .max(300, 'название не должно превышать 300 символов'),

    gender: genderSchema,

    slug: z
        .string({
            required_error: 'укажите slug',
            invalid_type_error: 'slug должен быть строкой',
        })
        .trim()
        .min(1, 'slug не может быть пустым')
        .max(160, 'slug не должен превышать 160 символов'),

    article: productArticleSchema,

    sellerPlatform: sellerPlatformSchema.optional().nullable(),

    poizonSpuId: z
        .string({ invalid_type_error: 'poizonSpuId должен быть строкой' })
        .trim()
        .min(1, 'poizonSpuId не может быть пустым')
        .max(120, 'poizonSpuId не должен превышать 120 символов')
        .optional()
        .nullable(),

    tags: z.array(
        z
            .string({ invalid_type_error: 'тег должен быть строкой' })
            .trim()
            .min(1, 'тег не может быть пустым')
            .max(50, 'тег не должен превышать 50 символов'),
        {
            required_error: 'нужно указать массив тегов',
            invalid_type_error: 'теги должны быть массивом строк',
        },
    ),

    hasPersonOnFirstImage: z.boolean({ invalid_type_error: 'значение должно быть true/false' }).optional(),

    bookmark: z.boolean({ invalid_type_error: 'значение должно быть true/false' }).optional(),

    description: z
        .string({ invalid_type_error: 'описание должно быть строкой' })
        .trim()
        .max(20000, 'описание не должно превышать 20000 символов')
        .optional()
        .nullable(),

    imageForInternalUser: z
        .string({ invalid_type_error: 'imageForInternalUser должен быть строкой' })
        .trim()
        .optional()
        .nullable(),

    imageForInternalUserNote: z
        .string({
            invalid_type_error: 'imageForInternalUserNote должен быть строкой',
        })
        .trim()
        .optional()
        .nullable(),

    images: z.array(imageItemSchema).min(1, 'нужна хотя бы одна картинка'),
    categories: z.array(categoryLinkSchema).min(1, 'нужна хотя бы одна категория'),
    selectionLists: z.array(selectionListLinkSchema).default([]),

    brands: z.array(uuidSchema),
    colors: z.array(uuidSchema),

    sizeChartId: uuidSchema,

    prices: z.array(priceItemSchema),
});

export type ProductObject = z.infer<typeof productObject>;

export function productRefinement(data: Omit<ProductObject, 'slug' | 'article'>, ctx: RefinementCtx): void {
    const seenPriceRowIds = new Set<string>();

    data.prices.forEach((p, i) => {
        const key = p.rowId;

        if (seenPriceRowIds.has(key)) {
            ctx.addIssue({
                code: 'custom',
                path: ['prices', i, 'rowId'],
                message: 'дублируется цена для размера',
            });
        } else {
            seenPriceRowIds.add(key);
        }
    });

    if (data.prices.length > 0 && !data.sizeChartId) {
        ctx.addIssue({
            code: 'custom',
            path: ['sizeChartId'],
            message: 'для размеров по размерной сетке нужно выбрать sizeChart',
        });
    }

    const seenImgIds = new Set<string>();
    const seenImgPos = new Set<number>();
    data.images.forEach((img, i) => {
        if (seenImgIds.has(img.id)) {
            ctx.addIssue({
                code: 'custom',
                path: ['images', i, 'id'],
                message: 'картинка повторяется',
            });
        } else {
            seenImgIds.add(img.id);
        }
        if (seenImgPos.has(img.position)) {
            ctx.addIssue({
                code: 'custom',
                path: ['images', i, 'position'],
                message: 'позиция картинки должна быть уникальной',
            });
        } else {
            seenImgPos.add(img.position);
        }
    });

    const seenCatIds = new Set<string>();
    data.categories.forEach((c, i) => {
        if (seenCatIds.has(c.id)) {
            ctx.addIssue({
                code: 'custom',
                path: ['categories', i, 'id'],
                message: 'категория повторяется',
            });
        } else {
            seenCatIds.add(c.id);
        }
    });

    const seenSelIds = new Set<string>();
    data.selectionLists.forEach((s, i) => {
        if (seenSelIds.has(s.id)) {
            ctx.addIssue({
                code: 'custom',
                path: ['selectionLists', i, 'id'],
                message: 'подборка повторяется',
            });
        } else {
            seenSelIds.add(s.id);
        }
    });
}

export const productInputSchema = productObject.superRefine(productRefinement);

export type ProductInputSchema = z.infer<typeof productInputSchema>;

export type ProductImageItemSchema = z.infer<typeof imageItemSchema>;
